/**
 * Provides API to work on semantic URLs.
 * These APIs are used for building semantic URLs
 * @module SemanticUrlUtils
 */
import type { RemoteCaseActionObject } from '../globals';
import {
  ACTION_OPENWORKBYHANDLE,
  ACTION_SHOWDATA,
  ACTION_SHOWVIEW,
  ACTION_GETOBJECT,
  ACTION_SHOWINSIGHT,
  ACTION_SHOWDASHBOARD
} from '../store/state/actions';

import RouteParser from './route-parser';

/**
 * This API obtains the resolved semantic URL for a route key. (example: openWorkByHandle)
 * @function getResolvedSemanticURL
 * @param routeKey - action type supported by semantic URL utilities
 * @param payload - unique identifiers to match with app routes to get semantic URLs
 * @param params - dynamic URL query parameters and values
 * @returns resolved semantic url or empty string
 * @example <caption>Example for getResolvedSemanticURL()</caption>
 * // In this example, the API takes the supported action from the route key,
 * // identifies the app route from the payload,
 * // assigns the value to the query parameter, and returns the resolved semantic URL.
 * const semanticUrlUtils = PCore.getSemanticUrlUtils();
 * const routeKey = semanticUrlUtils.getActins().ACTION_OPENWORKBYHANDLE;
 * const payload = {caseClassName: "ON8TTL-MyApp-Work-MyCase"};
 * const params = {workID: "C-007"}
 * const resolvedURL = semanticUrlUtils.getResolvedSemanticURL(routeKey, payload, params);
 */
const getResolvedSemanticURL = (routeKey: string, payload: any, params: any): string => {
  return RouteParser.getResolvedSemanticURL(routeKey, payload, params);
};

/**
 * This API updates the document title and semantic url
 * @example <caption>Example for updateSemanticURLAndDocumentTitle()</caption>
 * // this example will update document title and semantic url
 * const data = {key: 'I-20001', caseClassName: 'ON8TTL-MyApp-Work-MyCase', isRemoteCase: true, actionName: 'openWorkByHandle'};
 * PCore.getSemanticUrlUtils().updateSemanticURLAndDocumentTitle(data);
 * @private
 */
const updateSemanticURLAndDocumentTitle = (data: RemoteCaseActionObject) => {
  return RouteParser.updateSemanticURLAndDocumentTitle(data);
};

/**
 * This API obtains actions supported by semantic URL utilities.
 * @returns object of all the supported actions
 * @example <caption>Example for getActions()</caption>
 * // this example will return an object containing all the actions supported by semanticURLUtils
 * const semanticUrlUtils = PCore.getSemanticUrlUtils();
 * const { ACTION_OPENWORKBYHANDLE } = semanticUrlUtils.getActions();
 */
const getActions = () => {
  return {
    ACTION_OPENWORKBYHANDLE,
    ACTION_SHOWDATA,
    ACTION_SHOWVIEW,
    ACTION_GETOBJECT,
    ACTION_SHOWINSIGHT,
    ACTION_SHOWDASHBOARD
  };
};

/**
 * @private
 * Generates a semantic URL for toaster using caseTypeID and caseID.
 *
 * @param {string} caseTypeID - The identifier for the case type or class name.
 * @param {string} caseID - The unique identifier for the case or object.
 * @param {boolean} [isObjectType=false] - Flag indicating whether the ID refers to an object (true) or a case (false).
 * @returns {string} The resolved semantic URL.
 */
const getSemanticURLForToaster = (caseTypeID: string, caseID: string, isObjectType = false) => {
  const resourcePayload = {
    caseClassName: caseTypeID
  };

  const resourceParams = {
    [isObjectType ? 'objectID' : 'workID']: caseID
  };

  return getResolvedSemanticURL(
    isObjectType ? ACTION_GETOBJECT : ACTION_OPENWORKBYHANDLE,
    resourcePayload,
    resourceParams
  );
};

export default {
  getResolvedSemanticURL,
  getActions,
  updateSemanticURLAndDocumentTitle,
  getSemanticURLForToaster
};
